import { computed, action, observable } from 'mobx';
import { ViewController } from '@estee/elc-service';
import { emit, events } from '@estee/elc-service-bus';
import { LoyaltyRepository, offerRedemptionType } from '~repositories/LoyaltyRepository';
import { IBenefitsContent, IBenefitsInfo, IBenefitsLink, IBenefitPromotionOffer } from '~views/dashboard/loyalty-benefits/ILoyaltyBenefits';
import { ILoyaltyRPCRequest } from '~api/interfaces/ILoyaltyRpcAPI';
import { IBirthDate } from '~exported/loyalty-birthday/ILoyaltyBirthday';
import Benefits from '~domain/entities/LoyaltyBenefitsInfo';
import { PriorityOrderSort, CategoryOrderSort } from '~utils/OffersSorting';
import { AnalyticalTrackingUseCase } from '~use-cases/AnalyticalTrackingUseCase';
import { eventTag, ILoyaltyEventData } from '~utils/Analytics';
import { updateCartEvent } from '~internal/utils/CartUtils';

export interface IConfigParamsBenefits {
    benefits: IBenefitsContent;
}

class LoyaltyBenefitsController extends ViewController {
    @observable benefitOfferType: string;
    @observable birthdayOfferType: string;
    @observable isAddMyBirthdayModalOpen: boolean = false;
    @observable private configParams: IConfigParamsBenefits;
    private benefitsPromotionData: IBenefitPromotionOffer[] = [];

    private loyaltyRepository: LoyaltyRepository;

    constructor(loyaltyRepository: LoyaltyRepository) {
        super();
        this.loyaltyRepository = loyaltyRepository;
    }

    @action
    public setConfigParams = (configParams: IConfigParamsBenefits) => {
        this.configParams = configParams;
    };

    @action
    public loaded = async (benefitOfferType: string, birthdayOfferType?: string) => {
        this.isLoading = true;

        await this.loyaltyRepository.fetchBenefitsData(benefitOfferType);

        this.benefitOfferType = benefitOfferType;
        this.birthdayOfferType = birthdayOfferType || '';

        setTimeout(() => {
            this.triggerBenefitViewPromotionEvent();
        }, 100);
        this.isLoading = false;
    };

    @action
    public handleRedeemOffer = async (applyOfferCode: ILoyaltyRPCRequest) => {
        this.isLoading = true;

        await this.loyaltyRepository.redeemOffer(
            applyOfferCode,
            this.benefitOfferType,
            offerRedemptionType.benefits
        );

        const rewardsMessageData = this.loyaltyRepository?.offerRedemptionMessageData?.rewards;
        const offerSuccessMessageFromConfig = this.configParams?.benefits?.offerSuccessMessage?.text;
        if (
            offerSuccessMessageFromConfig &&
            rewardsMessageData?.type === 'success' &&
            rewardsMessageData?.redemptionAction === 'applied'
        ) {
            this.loyaltyRepository.setOfferRedemptionMessage({
                ...rewardsMessageData,
                content: [offerSuccessMessageFromConfig]
            });
        }

        const { offerCode, offerTitle, promoPosition } = applyOfferCode;
        const redeemOffer = this.loyaltyRepository?.benefitsOfferInfo?.inCartStatus;

        if (redeemOffer) {
            emit(events.LOYALTY_BENEFITS_REDEEM_CLICKED, offerCode);
            if (offerCode) {
                const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
                    {
                        event_name: 'select_promotion',
                        enh_action: 'promo_click',
                        event_action: 'loyalty_promotion_click',
                        event_label: 'loyalty_redeem_click_benefits',
                        promo_pos: [`${promoPosition}`],
                        promo_creative: ['benefits'],
                        promo_name: [`${offerTitle?.toLocaleLowerCase()}`],
                        promo_id: [`module-benefits-${offerCode}`]
                    },
                    eventTag.LOYALTY_ACTIONS_TRACKING
                );
                LoyaltyEventTracking.execute();
            }
        }

        updateCartEvent();
        this.isLoading = false;
    };

    @action
    public handleRemoveOffer = async (offerCode: string) => {
        this.isLoading = true;

        await this.loyaltyRepository.removeOffer(
            offerCode,
            this.benefitOfferType,
            offerRedemptionType.benefits
        );

        const rewardsMessageData = this.loyaltyRepository?.offerRedemptionMessageData?.benefits;
        const offerRemovedMessageFromConfig = this.configParams?.benefits?.offerRemovedMessage?.text;
        if (
            offerRemovedMessageFromConfig &&
            rewardsMessageData?.type === 'success' &&
            rewardsMessageData?.redemptionAction === 'removed'
        ) {
            this.loyaltyRepository.setOfferRedemptionMessage({
                ...rewardsMessageData,
                content: [offerRemovedMessageFromConfig]
            });
        }

        emit(events.LOYALTY_BENEFITS_REMOVE_CLICKED, offerCode);
        if (offerCode) {
            const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
                { event_name: 'loyalty_benefits', event_action: 'benefit_removed', event_label: offerCode },
                eventTag.LOYALTY_ACTIONS_TRACKING
            );
            LoyaltyEventTracking.execute();
        }

        updateCartEvent();
        this.isLoading = false;
    };

    @action
    public handleAddMyBirthday = async (birthday: IBirthDate) => {
        this.isLoading = true;

        await this.loyaltyRepository.setUserBirthdate(birthday);

        // Once birthday is updated, refresh the benefits and birthday offers available to the user
        await Promise.all([
            this.loyaltyRepository.fetchLoyaltyUserData(),
            this.loyaltyRepository.fetchBirthdayData(this.birthdayOfferType),
            this.loyaltyRepository.fetchBenefitsData(this.benefitOfferType)
        ]);

        const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
            { event_name: 'loyalty_benefits', event_action: 'cta_click', event_label: 'birthday form submit' },
            eventTag.LOYALTY_ACTIONS_TRACKING
        );
        LoyaltyEventTracking.execute();

        this.isLoading = false;
    };

    public onShopNowLinkClick = (linkLabel: string) => {
        emit(events.LOYALTY_SHOP_NOW_CLICKED, linkLabel);
        if (linkLabel) {
            const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
                { event_name: 'loyalty_benefits', event_action: 'cta_click', event_label: linkLabel },
                eventTag.LOYALTY_ACTIONS_TRACKING
            );
            LoyaltyEventTracking.execute();
        }
    };

    public onAllBenefitsLinkClick = (linkData: IBenefitsLink) => {
        const { label } = linkData;

        emit(events.LOYALTY_ALL_BENEFITS_CLICKED, label);
        if (label) {
            const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
                { event_name: 'loyalty_benefits', event_action: 'cta_click', event_label: label },
                eventTag.LOYALTY_ACTIONS_TRACKING
            );
            LoyaltyEventTracking.execute();
        }
    };

    public onBenefitsViewDetailsClick = (ctaLabel: string) => {
        if (ctaLabel) {
            const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
                { event_name: 'loyalty_benefits', event_action: 'cta_click', event_label: ctaLabel },
                eventTag.LOYALTY_ACTIONS_TRACKING
            );
            LoyaltyEventTracking.execute();
        }
    };

    public onBenefitsTitleClick = (promoPosition: number) => {
        const promoData = this.benefitsPromotionData.find(promotionData => promotionData.promoPosition === promoPosition);

        if (promoData?.offerCode) {
            const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
                {
                    event_name: 'select_promotion',
                    event_action: 'promotion click',
                    enh_action: 'promo_click',
                    event_label: promoData.offerCode,
                    promo_pos: [`${promoPosition}`],
                    promo_creative: ['benefits'],
                    promo_name: [`${promoData.offerTitle?.toLocaleLowerCase()}`],
                    promo_id: [`module-benefits-${promoData.offerCode}`]
                },
                eventTag.LOYALTY_ACTIONS_TRACKING
            );
            LoyaltyEventTracking.execute();
        }
    };

    @action
    public onBenefitsArrowClick(eLabel: string) {
        const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
            { event_name: 'loyalty_benefits', event_action: 'arrow_click', event_label: eLabel },
            eventTag.LOYALTY_ACTIONS_TRACKING
        );
        LoyaltyEventTracking.execute();
    }

    @action
    public toggleAddMyBirthdayModal = (ctaLabel: string) => {
        this.isAddMyBirthdayModalOpen = !this.isAddMyBirthdayModalOpen;
        const modalStaus = this.isAddMyBirthdayModalOpen ? 'Open' : 'Close';
        const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
            { event_name: 'loyalty_benefits', event_action: 'cta_click', event_label: `${ctaLabel} - ${modalStaus}` },
            eventTag.LOYALTY_ACTIONS_TRACKING
        );
        LoyaltyEventTracking.execute();
    };

    @action
    public setBenefitViewPromotionData = (sliderItem: IBenefitsInfo, offerMessage: string, promoPosition: number) => {
        const { offerCode, offerTitle, meetsCriteria, skuID} = sliderItem;

        this.benefitsPromotionData?.push({ offerCode, offerTitle, meetsCriteria, offerMessage, skuID, promoPosition});
    };

    @action
    public triggerBenefitViewPromotionEvent = () => {
        const eventData: ILoyaltyEventData = {
            event_name: 'view_promotion',
            event_category: 'ecommerce',
            event_action: 'loyalty_promo_impression',
            event_label: 'loyalty_promo_impression',
            promo_impression_list: [],
            promo_impression_offer_code: [],
            promo_impression_name: [],
            promo_impression_status: [],
            promo_impression_message: [],
            promo_impression_sku: []
        };

        this.benefitsPromotionData.forEach( (promotion) => {
            const offerStatus = promotion.meetsCriteria ? 'active' : 'disbaled';
            const skuID = promotion.skuID || null;

            eventData.promo_impression_list?.push('benefits');
            eventData.promo_impression_offer_code?.push(`${promotion.offerCode}`);
            eventData.promo_impression_name?.push(`${promotion.offerTitle}`);
            eventData.promo_impression_status?.push(`${offerStatus}`);
            eventData.promo_impression_message?.push(`${promotion.offerMessage}`);
            eventData.promo_impression_sku?.push(skuID);
        });

        const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
            eventData,
            eventTag.LOYALTY_ACTIONS_TRACKING
        );
        LoyaltyEventTracking.execute();
    };

    @computed
    public get data() {
        let benefitsInfo: IBenefitsInfo[] = [];
        if (this.configParams?.benefits.sorting?.enable && this.loyaltyRepository?.benefitsInfo?.benefitsDataFormatted) {
            const { priorityOrder, categoryOrder } = this.configParams.benefits.sorting;
            benefitsInfo = Benefits.getSortedBenefitsInfo(this.loyaltyRepository.benefitsInfo.benefitsDataFormatted,
                priorityOrder as PriorityOrderSort[], categoryOrder as CategoryOrderSort[]).benefitsDataFormatted;
        } else {
            benefitsInfo = this.loyaltyRepository?.benefitsInfo?.benefitsDataFormatted;
        }

        return {
            benefitsInfo: this.filterBirthdayOffer(benefitsInfo),
            benefitsOfferInfo: this.loyaltyRepository?.benefitsOfferInfo,
            offerRedemptionMessages: this.loyaltyRepository?.offerRedemptionMessageData,
            isLoading: this.isLoading
        };
    }

    @computed
    public get configs() {
        return this.configParams;
    }

    @computed
    public get isBirthdateOnFile() {
        const {birthDay, birthMonth} = this.loyaltyRepository.loyaltyInfo;

        return (birthDay && birthMonth) ? true : false;
    }

    @computed
    public get hasEligibleBirthdayOffers() {
        return this.loyaltyRepository.hasEligibleBirthdayOffers;
    }

    private filterBirthdayOffer(benefitsInfo: IBenefitsInfo[]) {
        const addMyBirthdayConfig = this.configParams.benefits.addMyBirthday;
        if (benefitsInfo) {
            return benefitsInfo.filter(benefit => {
                if (addMyBirthdayConfig?.birthdayOfferCodes?.includes(benefit.offerCode)) {
                    return !this.loyaltyRepository.hasEligibleBirthdayOffers;
                } else {
                    return true;
                }
            });
        } else {
            return benefitsInfo;
        }
    }

}

export default LoyaltyBenefitsController;
